import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Button from '@mui/material/Button';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';
import {
  DEFAULT_GRID_PREFERENCE,
  DEFAULT_SECTION_HEIGHT,
  RequestsColumnsPopover,
  applyColumnOrder,
  clampSectionHeight,
  sanitizeGridPreference,
} from './RequestsColumnsHelper';

const RequestsSection = ({
  sectionKey,
  title,
  rows,
  columns,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  searchFieldSx,
  expanded,
  onExpandedChange,
  height = DEFAULT_SECTION_HEIGHT,
  onHeightChange,
  isFullscreen = false,
  onToggleFullscreen,
  hidden = false,
  gridPreference,
  onUpdateGridPreference,
  isDarkMode,
  isMobile,
  onRowClick,
  disableRowSelectionOnClick = true,
}) => {
  const [columnsPanelAnchorEl, setColumnsPanelAnchorEl] = useState(null);
  const resizeStateRef = useRef(null);

  const columnFields = useMemo(() => columns.map((column) => column.field), [columns]);

  const sanitizedPreference = useMemo(
    () => sanitizeGridPreference(gridPreference ?? DEFAULT_GRID_PREFERENCE, columnFields),
    [gridPreference, columnFields],
  );

  const columnsByField = useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  );

  const orderedColumns = useMemo(
    () => applyColumnOrder(columns, sanitizedPreference.columnOrder),
    [columns, sanitizedPreference.columnOrder],
  );

  const displayColumns = useMemo(
    () => (isMobile ? scaleColumnsForMobile(orderedColumns) : orderedColumns),
    [isMobile, orderedColumns],
  );

  const columnsPanelOpen = Boolean(columnsPanelAnchorEl);
  const gridHeight = clampSectionHeight(height);

  const updatePreference = useCallback((updater) => {
    onUpdateGridPreference((currentPreference) => {
      const sanitized = sanitizeGridPreference(currentPreference ?? DEFAULT_GRID_PREFERENCE, columnFields);
      const nextPreference = typeof updater === 'function' ? updater(sanitized) : updater;
      return sanitizeGridPreference(nextPreference, columnFields);
    });
  }, [columnFields, onUpdateGridPreference]);

  const handleColumnsPanelToggle = (event) => {
    event.stopPropagation();
    setColumnsPanelAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleColumnsPanelClose = () => {
    setColumnsPanelAnchorEl(null);
  };

  const handleColumnVisibilityModelChange = useCallback((model) => {
    updatePreference((currentPreference) => ({
      ...currentPreference,
      columnVisibilityModel: model,
    }));
  }, [updatePreference]);

  const handleColumnVisibilityToggle = useCallback((field) => {
    updatePreference((currentPreference) => {
      const isVisible = currentPreference.columnVisibilityModel[field] !== false;
      return {
        ...currentPreference,
        columnVisibilityModel: {
          ...currentPreference.columnVisibilityModel,
          [field]: !isVisible,
        },
      };
    });
  }, [updatePreference]);

  const handleReorderColumns = useCallback((activeField, overField) => {
    updatePreference((currentPreference) => {
      const nextOrder = [...currentPreference.columnOrder];
      const fromIndex = nextOrder.indexOf(activeField);
      const toIndex = nextOrder.indexOf(overField);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return currentPreference;

      const [movedField] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, movedField);
      return {
        ...currentPreference,
        columnOrder: nextOrder,
      };
    });
  }, [updatePreference]);

  const handleResetColumns = useCallback(() => {
    updatePreference({
      columnOrder: columnFields,
      columnVisibilityModel: {},
    });
  }, [updatePreference, columnFields]);

  const handleResizeStart = useCallback((event) => {
    if (isFullscreen || !onHeightChange) return;
    event.preventDefault();
    event.stopPropagation();

    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    resizeStateRef.current = {
      startY: clientY,
      startHeight: clampSectionHeight(height),
    };

    const handleMove = (moveEvent) => {
      if (!resizeStateRef.current) return;
      const moveY = moveEvent.touches?.[0]?.clientY ?? moveEvent.clientY;
      const delta = moveY - resizeStateRef.current.startY;
      onHeightChange(clampSectionHeight(resizeStateRef.current.startHeight + delta));
    };

    const handleEnd = () => {
      resizeStateRef.current = null;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
  }, [height, isFullscreen, onHeightChange]);

  useEffect(() => () => {
    resizeStateRef.current = null;
  }, []);

  if (hidden) return null;

  const headerControls = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        pr: isFullscreen ? 0 : 2,
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
        {title} ({rows.length})
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => {
            e.stopPropagation();
            onSearchChange(e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.15rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            ...searchFieldSx,
            width: { xs: '100%', sm: '280px' },
            '& .MuiOutlinedInput-root': {
              ...(searchFieldSx?.['& .MuiOutlinedInput-root'] || {}),
              borderRadius: 999,
              height: 36,
              backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
              boxShadow: isDarkMode
                ? '0 1px 2px rgba(0,0,0,0.35)'
                : '0 1px 2px rgba(0,0,0,0.06)',
              '& fieldset': {
                borderColor: isDarkMode ? '#475569' : '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: isDarkMode ? '#64748b' : '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? '#64748b' : '#d1d5db',
                borderWidth: '1px',
              },
            },
          }}
        />
        <Tooltip title="Choose which columns are visible and reorder them">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon sx={{ fontSize: '1.1rem !important' }} />}
            aria-label={`Manage columns for ${title}`}
            onClick={handleColumnsPanelToggle}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              height: 36,
              borderRadius: 999,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderColor: isDarkMode ? '#475569' : '#e0e0e0',
              backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
              color: 'text.secondary',
              boxShadow: isDarkMode
                ? '0 1px 2px rgba(0,0,0,0.35)'
                : '0 1px 2px rgba(0,0,0,0.06)',
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': {
                backgroundColor: isDarkMode ? '#475569' : '#e5e7eb',
                borderColor: isDarkMode ? '#64748b' : '#d1d5db',
                color: 'text.primary',
              },
            }}
          >
            Columns
          </Button>
        </Tooltip>
        <Tooltip title={isFullscreen ? 'Exit full screen' : 'Expand this section to fill the page'}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              isFullscreen
                ? <FullscreenExitIcon sx={{ fontSize: '1.1rem !important' }} />
                : <FullscreenIcon sx={{ fontSize: '1.1rem !important' }} />
            }
            aria-label={isFullscreen ? `Exit full screen for ${title}` : `Full screen ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFullscreen?.(sectionKey);
            }}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              height: 36,
              borderRadius: 999,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderColor: isFullscreen
                ? (isDarkMode ? '#fff' : '#111')
                : (isDarkMode ? '#475569' : '#e0e0e0'),
              backgroundColor: isFullscreen
                ? (isDarkMode ? '#fff' : '#111')
                : (isDarkMode ? '#0f172a' : '#ffffff'),
              color: isFullscreen
                ? (isDarkMode ? '#000' : '#fff')
                : 'text.secondary',
              boxShadow: isDarkMode
                ? '0 1px 2px rgba(0,0,0,0.35)'
                : '0 1px 2px rgba(0,0,0,0.06)',
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': {
                backgroundColor: isFullscreen
                  ? (isDarkMode ? '#e5e7eb' : '#333')
                  : (isDarkMode ? '#475569' : '#e5e7eb'),
                borderColor: isFullscreen
                  ? (isDarkMode ? '#fff' : '#111')
                  : (isDarkMode ? '#64748b' : '#d1d5db'),
                color: isFullscreen
                  ? (isDarkMode ? '#000' : '#fff')
                  : 'text.primary',
              },
            }}
          >
            {isFullscreen ? 'Exit' : 'Full screen'}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );

  const dataGrid = (
    <DataGrid
      rows={rows}
      columns={displayColumns}
      density={isMobile ? 'compact' : 'standard'}
      columnVisibilityModel={sanitizedPreference.columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      onRowClick={onRowClick}
      sx={{
        border: 0,
        height: '100%',
        width: '100%',
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: 'text.secondary',
        },
        '& .MuiDataGrid-cell': {
          fontSize: '0.875rem',
          color: 'text.primary',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-row': {
          cursor: onRowClick ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
          },
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
        },
      }}
    />
  );

  const columnsPopover = (
    <RequestsColumnsPopover
      open={columnsPanelOpen}
      anchorEl={columnsPanelAnchorEl}
      onClose={handleColumnsPanelClose}
      isDarkMode={isDarkMode}
      activeGridPreference={sanitizedPreference}
      columnsByField={columnsByField}
      onToggleColumnVisibility={handleColumnVisibilityToggle}
      onReorderColumns={handleReorderColumns}
      onResetColumns={handleResetColumns}
    />
  );

  // Fullscreen: plain flex panel (avoid Accordion Collapse, which breaks DataGrid height)
  if (isFullscreen) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
          }}
        >
          {headerControls}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {dataGrid}
        </Box>
        {columnsPopover}
      </Box>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onExpandedChange}
      sx={{
        boxShadow: 'none',
        border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        borderRadius: 2,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        {headerControls}
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, pt: 1 }}>
        <Box
          sx={{
            height: gridHeight,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            {dataGrid}
          </Box>

          {onHeightChange && (
            <Box
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
              role="separator"
              aria-orientation="horizontal"
              aria-label={`Resize ${title} section`}
              title="Drag to resize"
              sx={{
                height: 36,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'ns-resize',
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                color: 'text.secondary',
                userSelect: 'none',
                touchAction: 'none',
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#334155' : '#f0f0f0',
                  color: 'text.primary',
                  '& .resize-handle-pill': {
                    backgroundColor: isDarkMode ? '#475569' : '#e5e7eb',
                    borderColor: isDarkMode ? '#64748b' : '#d1d5db',
                  },
                },
                '&:active': {
                  backgroundColor: isDarkMode ? '#334155' : '#ebebeb',
                },
              }}
            >
              <Box
                className="resize-handle-pill"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                  boxShadow: isDarkMode
                    ? '0 1px 2px rgba(0,0,0,0.35)'
                    : '0 1px 2px rgba(0,0,0,0.06)',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <DragIndicatorIcon
                  sx={{
                    fontSize: 22,
                    transform: 'rotate(90deg)',
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  Drag to resize
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>

      {columnsPopover}
    </Accordion>
  );
};

export default RequestsSection;
